import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import client from "../api/client";

const AuthContext = createContext(null);

const tokenStorage = () => localStorage.getItem("access_token") ? localStorage : sessionStorage;
const clearStoredSession = () => {
  localStorage.removeItem("access_token");
  sessionStorage.removeItem("access_token");
  localStorage.removeItem("current_user");
  localStorage.removeItem("Name");
  localStorage.removeItem("profileURL");
};

const initialDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("current_user") || "null"); } catch (_) { return null; }
  });
  const [initializing, setInitializing] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [companySwitching, setCompanySwitching] = useState(false);
  const [dateRange, setDateRangeState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("workspace_date_range")) || initialDateRange(); } catch (_) { return initialDateRange(); }
  });

  const persistUser = useCallback((nextUser) => {
    setUser(nextUser);
    if (nextUser) {
      localStorage.setItem("current_user", JSON.stringify(nextUser));
      localStorage.setItem("Name", [nextUser.first_name, nextUser.last_name].filter(Boolean).join(" ") || nextUser.email);
    } else {
      localStorage.removeItem("current_user");
      localStorage.removeItem("Name");
    }
  }, []);

  const expireSession = useCallback(() => {
    clearStoredSession();
    setUser(null);
    setSessionExpired(true);
    setInitializing(false);
  }, []);

  useEffect(() => {
    window.addEventListener("auth:expired", expireSession);
    const token = sessionStorage.getItem("access_token") || localStorage.getItem("access_token");
    if (!token) {
      setInitializing(false);
      return () => window.removeEventListener("auth:expired", expireSession);
    }
    client.get("/auth/me")
      .then(({ data }) => persistUser(data.user || data))
      .catch(() => expireSession())
      .finally(() => setInitializing(false));
    return () => window.removeEventListener("auth:expired", expireSession);
  }, [expireSession, persistUser]);

  const login = useCallback(async (credentials, remember) => {
    const { data } = await client.post("/auth/login", credentials);
    clearStoredSession();
    (remember ? localStorage : sessionStorage).setItem("access_token", data.token);
    persistUser(data.user);
    setSessionExpired(false);
    return data.user;
  }, [persistUser]);

  const logout = useCallback(async () => {
    try { await client.post("/auth/logout"); } catch (_) { /* Local sign-out must always complete. */ }
    clearStoredSession();
    setUser(null);
    setSessionExpired(false);
  }, []);

  const switchCompany = useCallback(async (companyId) => {
    if (!companyId || String(companyId) === String(user?.company?.id)) return;
    setCompanySwitching(true);
    try {
      const storage = tokenStorage();
      const { data } = await client.post("/auth/switch-company", { company_id: companyId });
      localStorage.removeItem("access_token");
      sessionStorage.removeItem("access_token");
      storage.setItem("access_token", data.token);
      persistUser(data.user);
    } finally {
      setCompanySwitching(false);
    }
  }, [persistUser, user]);

  const setDateRange = useCallback((nextRange) => {
    setDateRangeState(nextRange);
    localStorage.setItem("workspace_date_range", JSON.stringify(nextRange));
  }, []);

  const hasPermission = useCallback((permission) => !permission || (user?.permissions || []).includes(permission), [user]);
  const value = useMemo(() => ({ user, initializing, sessionExpired, companySwitching, dateRange, setDateRange, login, logout, switchCompany, hasPermission }), [user, initializing, sessionExpired, companySwitching, dateRange, setDateRange, login, logout, switchCompany, hasPermission]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
};
